/* eslint-disable ember/classic-decorator-hooks, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Ember imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
// Internal imports
import PostType from '@linc-technologies/linced-common/enums/post-type';
import {
  filterBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class PostList extends Component {
  @action
  setPaginationIndex(value) {
    this.paginationIndex = value;
  }

  // Component versioning
  @tracked visualStyle = 'v1';

  @service
  router;

  @service
  store;

  PostType = PostType;
  @tracked filterTag = null;
  @tracked paginationIndex = 0; // zero indexed
  @tracked paginationMax = 25;
  trackReadState = false;
  readPostIds = [];

  // avatar allows controlling whose avatar is shown on the posts.
  // Allowed options: ['person', 'author']
  avatar = 'author';

  // disableActions allows disabling the actions menu, for a "read-only" list.
  disableActions = false;

  // prerenderComments determines whether we should prerender comments
  // so that they'll all be included if the page is printed.
  prerenderComments = true;

  @tracked
  filterParams = [];

  // addAuthor is a callback for filtering on author.
  addAuthor() {}

  // addGroup is a callback for filtering on group.
  addGroup() {}

  init() {
    super.init(...arguments);
    this.filterParams = [];
    let params = this.router.currentRoute.queryParams;
    if (!isEmpty(params.schoolId)) {
      this.store.findRecord('school', params.schoolId).then((school) => {
        this.filterParams = [
          ...this.filterParams,
          { type: 'schoolId', label: school.name },
        ];
      });
    }
    if (!isEmpty(params.authorId)) {
      this.store.findRecord('person', params.authorId).then((person) => {
        this.filterParams = [
          ...this.filterParams,
          { type: 'authorId', label: person.fullName },
        ];
      });
    }
    if (!isEmpty(params.groupId)) {
      this.store.findRecord('group', params.groupId).then((group) => {
        this.filterParams = [
          ...this.filterParams,
          { type: 'groupId', label: group.name },
        ];
      });
    }
  }

  @computed('posts.[]', 'filterTag.id')
  get filteredPosts() {
    if (!isEmpty(this.filterTag)) {
      // goals and tags are async hasManys, whose PromiseManyArrays have no
      // includes(); the ids are all that's needed.
      let id = this.filterTag.id;
      return this.posts.filter(
        (item) =>
          item.hasMany('goalIds').ids().includes(id) ||
          item.hasMany('tagIds').ids().includes(id),
      );
    } else {
      return this.posts;
    }
  }

  @computed('filteredPosts.[]', 'paginationIndex', 'paginationMax')
  get paginatedPosts() {
    let startIndex = this.paginationIndex * this.paginationMax;
    let endIndex = (this.paginationIndex + 1) * this.paginationMax; // not inclusive
    return this.filteredPosts.slice(startIndex, endIndex);
  }

  @computed('filteredPosts.[]', 'paginationIndex', 'paginationMax')
  get hasOlderPosts() {
    let endIndex = (this.paginationIndex + 1) * this.paginationMax; // not inclusive
    return this.filteredPosts.length > endIndex;
  }

  @action
  setFilterTag(tag) {
    if (isEmpty(this.filterTag)) {
      // click a filter
      tag.set('filteredOn', true);
      this.filterTag = tag;
    } else if (this.filterTag === tag) {
      // reclick same filter
      tag.set('filteredOn', false);
      this.filterTag = null;
    } else {
      // click a different filter
      tag.set('filteredOn', true);
      this.filterTag.set('filteredOn', false);
      this.filterTag = tag;
    }
  }

  @action
  setFilter(filterQuery, filterLabel, filterType) {
    if (
      isEmpty(this.filterParams) ||
      isEmpty(filterBy(this.filterParams, 'label', filterLabel))
    ) {
      if (
        !isEmpty(this.filterParams) &&
        findBy(this.filterParams, 'type', filterType)
      ) {
        let existing = findBy(this.filterParams, 'type', filterType);
        this.filterParams = this.filterParams.filter(
          (param) => param !== existing,
        );
      }
      this.filterParams = [
        ...this.filterParams,
        { type: filterType, label: filterLabel },
      ];
    }
    this.router.transitionTo({ queryParams: filterQuery.values });
  }

  @action
  removeFilter(filter) {
    this.filterParams = this.filterParams.filter((param) => param !== filter);
    let queryParam = null;
    switch (filter.type) {
      case 'schoolId':
        queryParam = { schoolId: null, groupId: null, authorId: null };
        this.filterParams = [];
        break;
      case 'authorId':
        if (isEmpty(this.filterParams)) {
          queryParam = { schoolId: null, authorId: null };
          break;
        }
        queryParam = { authorId: null };
        break;
      case 'groupId':
        if (isEmpty(this.filterParams)) {
          queryParam = { schoolId: null, groupId: null };
          break;
        }
        queryParam = { groupId: null };
        break;
    }
    this.router.transitionTo({ queryParams: queryParam });
  }

  willDestroy() {
    if (!isEmpty(this.filterTag)) {
      this.filterTag.set('filteredOn', false);
    }
    super.willDestroy(...arguments);
  }
}
